import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { SelectListControlItem, SelectListItem, ValidationModel } from '@shared/models';


export abstract class ServiceBase<T> {
  protected baseUrl: string;

  protected constructor(
    protected http: HttpClient,
    protected apiUrl: string,
    private controller: string,
  ) {
    this.baseUrl = apiUrl + controller;
  }

  /**
   * Retrieves an object array from the given endpoint.
   *
   * @param endpoint {string} - Endpoint to send the request to.
   * @param params {HttpParams | {[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>}} -
   * Url encoded query params to send the get request with.
   */
  public get(
    params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>; },
    endpoint: string = ''
  ): Observable<T[]> {
    return this.http.get<T[]>(`${this.baseUrl}/${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    });
  }

  /**
   * Retrieves one object from the API from the endpoint by route: apiUrl/controller/id.
   *
   * @param id {string} - the id of the desired object.
   */
  public getById(id: string): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}/${id}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Retrieves a given selectlist from the given endpoint.
   *
   * @param endpoint {string} - Endpoint to send the request to.
   * @param params {HttpParams | {[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>}} -
   * Url encoded query params to send the get request with.
   */
  public getRefSelectList(
    endpoint: string,
    params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> }
  ): Observable<{ selectItems: SelectListItem[], defaultItem: SelectListItem }> {

    return this.http
      .get<SelectListItem[]>(`${this.baseUrl}/${endpoint}`, {
        params,
        headers: {
          'Content-Type': 'application/json',
        },
      }).pipe(
        map(
          selectItems => {
            const selected = selectItems.find(item => item.isSelected);

            return { selectItems, defaultItem: selected };
          }
        )
      );
  }

  /**
 * Retrieves a given selectlist from the given endpoint.
 *
 * @param endpoint {string} - Endpoint to send the request to.
 * @param params {HttpParams | {[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>}} -
 * Url encoded query params to send the get request with.
 */
  public getRefSelectListControl(
    endpoint: string,
    params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> }
  ): Observable<{ selectItems: SelectListControlItem[], defaultItem: SelectListControlItem }> {

    return this.http
      .get<SelectListControlItem[]>(`${this.baseUrl}/${endpoint}`, {
        params,
        headers: {
          'Content-Type': 'application/json',
        },
      }).pipe(
        map(
          selectItems => {
            const selected = selectItems.find(item => item.isSelected);

            return { selectItems, defaultItem: selected };
          }
        )
      );
  }

  /**
   * Retrieves validation model.
   *
   * @param params {HttpParams | {[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>}} -
   * Url encoded query params to send the get request with.
   */
  public getValidation(
    params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>; }
  ): Observable<ValidationModel[]> {
    return this.http.get<ValidationModel[]>(`${this.baseUrl}/validation`, {
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    });
  }

  /**
   * Retrieves object with base data for create operation.
   *
   * @param params {HttpParams | {[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>}} -
   * Url encoded query params to send the get request with.
   */
  public createGet(
    params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>; }
  ): Observable<T[]> {
    return this.http.get<T[]>(`${this.baseUrl}/createGet`, {
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    });
  }

  /**
   * Retrieves object with base data for create operation.
   *
   * @param params {HttpParams | {[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>}} -
   * Url encoded query params to send the get request with.
   */
  public updateGet(
    params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>; }
  ): Observable<T[]> {
    return this.http.get<T[]>(`${this.baseUrl}/updateGet`, {
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    });
  }

  /**
   * Sends a post request with the data as payload.
   *
   * @param endpoint {string} - The endpoint.
   * @param data {T} - The data to send in the body of the request.
   * @param params {HttpParams | {[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>}} -
   * Url encoded query params to send the get request with.
   */
  public post(endpoint: string, data: T, params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>; }
  ): Observable<HttpResponse<Object>> {
    return this.http
      .post(`${this.baseUrl}/${endpoint}`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
        params,
        observe: 'response',
      });
  }

  /**
   * Create object - same function as "post", but given more clarity of when to use it
   *
   * @param data {T} - The data to send in the body of the request.
   * @param params {HttpParams | {[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>}} -
   * Url encoded query params to send the get request with.
   */

  public create(data: any, params?: any): Observable<HttpResponse<Object>> {
    return this.post('', data, params);
  }

  /**
   * Sends a put request to a given endpoint.
   *
   * @param endpoint {string} - The endpoint.
   * @param data {T} - The data to send in the body of the request.
   * @param params {HttpParams | {[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>}} -
   * Url encoded query params to send the get request with.
   */
  public put(endpoint: string, data: T, params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>; }
  ): Observable<HttpResponse<Object>> {
    return this.http
      .put(`${this.baseUrl}/${endpoint}`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
        params,
        observe: 'response',
      });
  }

  /**
   * Update object - same function as "put", but given more clarity of when to use it
   *
   * @param id {string} - id of updated object - the endpoint.
   * @param data {T} - The data to send in the body of the request.
   * @param params {HttpParams | {[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>}} -
   * Url encoded query params to send the get request with.
   */

  public update(id: string, data: any, params?: any): Observable<HttpResponse<Object>> {
    return this.put(id, data, params);
  }

  /**
   * Sends a delete request to the given endpoint.
   *
   * @param endpoint {string} - The endpoint.
   *
   * @example - Delete a selected object.
   * this.service.delete(this.getCurrentId())
   *  .subscribe((response) => console.log(response.status))
   */
  public delete(endpoint: string = ''): Observable<HttpResponse<Object>> {
    return this.http
      .delete(`${this.baseUrl}/${endpoint}`, {
        observe: 'response',
        headers: {
          'Content-Type': 'application/json',
        },
      });
  }

  getFile(fileName: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/file/${fileName}`, {
      responseType: 'blob',
    });
  }

  uploadFiles(data: { name: string; file: File }[]) {
    const formData: FormData = new FormData();
    data.forEach((d) => formData.append(d.name, d.file));

    return this.http.post<any>(`${this.baseUrl}/files`, formData, {
      observe: 'response',
    });
  }
}
